import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Plus, Trash2, Copy, ChevronUp, ChevronDown, GripVertical, AlertTriangle,
  Save, FileDown, Send, Eye, LayoutList, MessageSquare, BookmarkPlus,
} from "lucide-react";

const BLANK_ITEM = () => ({
  id: `new-${Math.random().toString(36).slice(2)}`, activity: "", custom: false,
  start_time: "", end_time: "", duration_min: "", participant_group: "All",
  location: "", instructions: "", equipment: "",
  display_in_email: true, display_on_public: true, display_in_dashboard: true,
});

const SEND_KINDS = [
  ["reminder", "Send Reminder"], ["full_details", "Send Full Event Details"],
  ["agenda_only", "Send Agenda Only"], ["schedule_update", "Send Schedule Update"],
  ["venue_change", "Send Venue Change"], ["weather_update", "Send Weather Update"],
  ["custom", "Send Custom Message"],
];

export default function CampAgendaManager({ camp, camps, onClose, onSaved }) {
  const [tab, setTab] = useState("agenda");
  const [cfg, setCfg] = useState({ activities: [], participant_groups: ["All"] });
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  // communications
  const [send, setSend] = useState({ kind: "reminder", subject: "", intro: "", closing: "", recipients: "registered", test_email: "" });
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const [{ data: cc }, { data: tpl }, { data: fresh }] = await Promise.all([
      api.get("/camps-config/agenda"),
      api.get("/admin/agenda-templates"),
      api.get(`/camps/${camp.id}`),
    ]);
    setCfg(cc); setTemplates(tpl.items || []);
    setItems((fresh.agenda || []).map((it) => ({ ...BLANK_ITEM(), ...it })));
  }, [camp.id]);
  useEffect(() => { load(); }, [load]);

  const setItem = (i, patch) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const addItem = () => setItems((arr) => [...arr, BLANK_ITEM()]);
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const dupItem = (i) => setItems((arr) => { const c = { ...arr[i], id: `new-${Math.random().toString(36).slice(2)}` }; const n = [...arr]; n.splice(i + 1, 0, c); return n; });
  const move = (i, dir) => setItems((arr) => { const j = i + dir; if (j < 0 || j >= arr.length) return arr; const n = [...arr]; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const onDrop = (i) => setItems((arr) => { if (dragIdx === null || dragIdx === i) return arr; const n = [...arr]; const [m] = n.splice(dragIdx, 1); n.splice(i, 0, m); return n; });

  const saveAgenda = async () => {
    setBusy(true);
    try {
      const clean = items.map((it, idx) => ({ ...it, order: idx, duration_min: it.duration_min ? Number(it.duration_min) : null }));
      await api.patch(`/camps/${camp.id}`, { agenda: clean });
      const { data } = await api.get(`/camps/${camp.id}/agenda/warnings`);
      setWarnings(data.warnings || []);
      toast.success("Agenda saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const applyTemplate = async (tid) => {
    if (!tid) return;
    if (items.length && !window.confirm("Replace the current agenda with this template?")) return;
    const { data } = await api.post(`/admin/camps/${camp.id}/apply-template/${tid}`);
    setItems((data.agenda || []).map((it) => ({ ...BLANK_ITEM(), ...it })));
    toast.success("Template applied");
  };
  const copyFrom = async (sid) => {
    if (!sid) return;
    if (items.length && !window.confirm("Replace the current agenda with that camp's agenda?")) return;
    const { data } = await api.post(`/admin/camps/${camp.id}/copy-agenda-from/${sid}`);
    setItems((data.agenda || []).map((it) => ({ ...BLANK_ITEM(), ...it })));
    toast.success("Agenda copied");
  };
  const saveAsTemplate = async () => {
    const name = window.prompt("Template name?");
    if (!name) return;
    await api.post("/admin/agenda-templates", { name, event_type: camp.type, description: `From ${camp.title}`, items });
    const { data } = await api.get("/admin/agenda-templates");
    setTemplates(data.items || []);
    toast.success("Saved as reusable template");
  };

  const runPreview = useCallback(async () => {
    try {
      const { data } = await api.post(`/admin/camps/${camp.id}/email-preview`, send);
      setPreview(data);
    } catch { /* ignore */ }
  }, [camp.id, send]);
  useEffect(() => { if (tab === "comms") runPreview(); }, [tab, runPreview]);

  const doSend = async (test) => {
    if (send.kind === "custom" && !send.subject.trim()) { toast.error("Enter a subject for a custom message"); return; }
    setBusy(true);
    try {
      const body = { ...send, test_email: test ? (send.test_email || "").trim() : "" };
      if (test && !body.test_email) { toast.error("Enter a test email address"); setBusy(false); return; }
      if (!test && !window.confirm(`Send this ${send.kind.replace("_", " ")} email to selected recipients?`)) { setBusy(false); return; }
      const { data } = await api.post(`/admin/camps/${camp.id}/send`, body);
      toast.success(test ? `Test sent to ${body.test_email}` : `${data.recipients} email(s) ${data.live ? "sent" : "queued"}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Send failed"); }
    finally { setBusy(false); }
  };

  const inp = "w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm";
  const tabBtn = (k, label, Icon) => (
    <button data-testid={`agenda-tab-${k}`} onClick={() => setTab(k)} className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold uppercase tracking-wider border-b-2 ${tab === k ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400"}`}><Icon className="w-4 h-4" /> {label}</button>
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" data-testid="camp-agenda-manager">
      <div className="bg-white max-w-5xl w-full max-h-[92vh] overflow-y-auto rounded-sm">
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-display text-xl uppercase tracking-tight">{camp.title}</h2>
            <p className="text-[11px] text-slate-400">{camp.type} · Times in {camp.timezone || "America/Toronto"}</p>
          </div>
          <button data-testid="agenda-close" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-sm"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex border-b border-slate-200 px-3">
          {tabBtn("agenda", "Agenda Builder", LayoutList)}
          {tabBtn("comms", "Communications", MessageSquare)}
        </div>

        {tab === "agenda" && (
          <div className="p-5 space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <select data-testid="apply-template-select" onChange={(e) => applyTemplate(e.target.value)} value="" className={`${inp} w-auto bg-white`}>
                <option value="">Apply template…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <select data-testid="copy-from-select" onChange={(e) => copyFrom(e.target.value)} value="" className={`${inp} w-auto bg-white`}>
                <option value="">Copy from camp…</option>
                {(camps || []).filter((c) => c.id !== camp.id).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </select>
              <button data-testid="save-as-template" onClick={saveAsTemplate} className="flex items-center gap-1 text-xs font-semibold border border-slate-300 px-3 py-1.5 rounded-sm hover:bg-slate-50"><BookmarkPlus className="w-3.5 h-3.5" /> Save as Template</button>
            </div>

            {warnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="agenda-warnings">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-800 mb-1"><AlertTriangle className="w-4 h-4" /> {warnings.length} warning(s)</div>
                {warnings.map((w, i) => <div key={i} className="text-[11px] text-amber-700">• {w.message}</div>)}
              </div>
            )}

            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={it.id} draggable onDragStart={() => setDragIdx(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                  data-testid={`agenda-item-${i}`} className="border border-slate-200 rounded-sm p-3 bg-slate-50/50">
                  <div className="flex items-start gap-2">
                    <div className="pt-2 cursor-grab text-slate-300"><GripVertical className="w-4 h-4" /></div>
                    <div className="flex-1 grid sm:grid-cols-12 gap-2">
                      <div className="sm:col-span-4">
                        <input list="activity-presets" data-testid={`agenda-activity-${i}`} placeholder="Activity" value={it.activity} onChange={(e) => setItem(i, { activity: e.target.value })} className={inp} />
                      </div>
                      <input data-testid={`agenda-start-${i}`} type="time" value={it.start_time || ""} onChange={(e) => setItem(i, { start_time: e.target.value })} className={`${inp} sm:col-span-2`} />
                      <input type="time" value={it.end_time || ""} onChange={(e) => setItem(i, { end_time: e.target.value })} className={`${inp} sm:col-span-2`} placeholder="End" />
                      <select value={it.participant_group} onChange={(e) => setItem(i, { participant_group: e.target.value })} className={`${inp} sm:col-span-2 bg-white`}>
                        {cfg.participant_groups.map((g) => <option key={g}>{g}</option>)}
                      </select>
                      <input placeholder="Station" value={it.location || ""} onChange={(e) => setItem(i, { location: e.target.value })} className={`${inp} sm:col-span-2`} />
                      <input placeholder="Instructions / notes" value={it.instructions || ""} onChange={(e) => setItem(i, { instructions: e.target.value })} className={`${inp} sm:col-span-6`} />
                      <input placeholder="Required equipment" value={it.equipment || ""} onChange={(e) => setItem(i, { equipment: e.target.value })} className={`${inp} sm:col-span-6`} />
                      <div className="sm:col-span-12 flex flex-wrap gap-4 text-[11px] text-slate-500">
                        <label className="flex items-center gap-1"><input data-testid={`agenda-email-toggle-${i}`} type="checkbox" checked={it.display_in_email} onChange={(e) => setItem(i, { display_in_email: e.target.checked })} /> Email</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={it.display_on_public} onChange={(e) => setItem(i, { display_on_public: e.target.checked })} /> Public page</label>
                        <label className="flex items-center gap-1"><input type="checkbox" checked={it.display_in_dashboard} onChange={(e) => setItem(i, { display_in_dashboard: e.target.checked })} /> Dashboard</label>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => move(i, -1)} className="p-1 hover:bg-slate-200 rounded-sm"><ChevronUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => move(i, 1)} className="p-1 hover:bg-slate-200 rounded-sm"><ChevronDown className="w-3.5 h-3.5" /></button>
                      <button data-testid={`agenda-dup-${i}`} onClick={() => dupItem(i)} className="p-1 hover:bg-slate-200 rounded-sm"><Copy className="w-3.5 h-3.5" /></button>
                      <button data-testid={`agenda-del-${i}`} onClick={() => removeItem(i)} className="p-1 hover:bg-slate-200 rounded-sm text-[#E63946]"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
              <datalist id="activity-presets">{cfg.activities.map((a) => <option key={a} value={a} />)}</datalist>
              {items.length === 0 && <p className="text-sm text-slate-400 text-center py-6">No agenda items yet. Add one or apply a template.</p>}
            </div>

            <div className="flex justify-between sticky bottom-0 bg-white py-3 border-t border-slate-100">
              <button data-testid="agenda-add-item" onClick={addItem} className="flex items-center gap-1 text-sm font-semibold border border-slate-300 px-3 py-2 rounded-sm hover:bg-slate-50"><Plus className="w-4 h-4" /> Add Activity</button>
              <button data-testid="agenda-save" onClick={saveAgenda} disabled={busy} className="flex items-center gap-1 bg-[#E63946] text-white px-5 py-2 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save Agenda"}</button>
            </div>
          </div>
        )}

        {tab === "comms" && (
          <div className="p-5 grid md:grid-cols-2 gap-5">
            <div className="space-y-3">
              <div>
                <label className="label-overline">Message Type</label>
                <select data-testid="send-kind" value={send.kind} onChange={(e) => setSend({ ...send, kind: e.target.value })} className={`${inp} mt-1 bg-white`}>
                  {SEND_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="label-overline">Subject {send.kind !== "custom" && <span className="text-slate-400 normal-case">(auto if blank)</span>}</label>
                <input data-testid="send-subject" value={send.subject} onChange={(e) => setSend({ ...send, subject: e.target.value })} className={`${inp} mt-1`} placeholder="Auto-generated from type" />
              </div>
              <div>
                <label className="label-overline">Intro Message</label>
                <textarea data-testid="send-intro" value={send.intro} onChange={(e) => setSend({ ...send, intro: e.target.value })} rows={2} className={`${inp} mt-1`} />
              </div>
              <div>
                <label className="label-overline">Closing Notes</label>
                <textarea data-testid="send-closing" value={send.closing} onChange={(e) => setSend({ ...send, closing: e.target.value })} rows={2} className={`${inp} mt-1`} />
              </div>
              <div>
                <label className="label-overline">Recipients</label>
                <select data-testid="send-recipients" value={send.recipients} onChange={(e) => setSend({ ...send, recipients: e.target.value })} className={`${inp} mt-1 bg-white`}>
                  <option value="registered">All registered (incl. pending)</option>
                  <option value="confirmed">Confirmed / paid only</option>
                </select>
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="label-overline">Test Email</label>
                  <input data-testid="send-test-email" value={send.test_email} onChange={(e) => setSend({ ...send, test_email: e.target.value })} className={`${inp} mt-1`} placeholder="you@email.com" />
                </div>
                <button data-testid="send-test" onClick={() => doSend(true)} disabled={busy} className="flex items-center gap-1 text-xs font-semibold border border-slate-300 px-3 py-2 rounded-sm hover:bg-slate-50 disabled:opacity-60"><Send className="w-3.5 h-3.5" /> Test</button>
              </div>
              <button data-testid="send-now" onClick={() => doSend(false)} disabled={busy} className="w-full flex items-center justify-center gap-1 bg-[#0A192F] text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"><Send className="w-4 h-4" /> {busy ? "Sending…" : "Send Now"}</button>
              <p className="text-[10px] text-slate-400">Scheduling (send later) is coming in Phase 2. The exact agenda is snapshotted into each email — editing the agenda later never changes emails already sent.</p>
            </div>
            <div>
              <div className="flex items-center gap-1.5 label-overline mb-2"><Eye className="w-4 h-4" /> Live Email Preview</div>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 truncate" data-testid="preview-subject">{preview?.subject || "…"}</div>
                <iframe title="preview" data-testid="preview-frame" srcDoc={preview?.html || "<p style='padding:16px;color:#888'>Loading…</p>"} className="w-full h-[420px] bg-white" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
